import {
    Box,
    Group,
    Loader,
    ScrollArea,
    Text,
    UnstyledButton,
    useMantineColorScheme,
} from "@mantine/core";
import { IconChevronsLeft, IconChevronsRight } from "@tabler/icons-react";
import { useRef, useState } from "react";
import { useDisclaimer } from "src/contexts/DisclaimerContext";
import { useDatasources } from "src/hooks/useDatasources";
import { getDisclaimerMessages } from "src/utils/referencesHandler";
import type { ChatVectorIndexOption, Reference } from "../db";
import { onMiddleClick, useArtifactHandler } from "../utils/artifactHandler";

const ARTIFACT_EXTENSIONS = [
    ".pdf",
    ".html",
    ".doc",
    ".docx",
    ".ppt",
    ".pptx",
    ".xls",
    ".xlsx",
    ".msg",
    ".csv",
    ".json",
    ".txt",
];

// Helper to get label from datasources
export const getDatasourceLabel = (
    dataSourceType: string,
    datasources: ChatVectorIndexOption[],
): string | undefined => {
    return datasources.find((ds) => ds.datasource_id === dataSourceType)?.label;
};

const getFileExtension = (url: string): string => {
    // Check if it's a web URL without a file extension
    if (url.startsWith("http") && !url.split("/").pop()?.includes(".")) {
        return "URL";
    }
    const lowerCaseUrl = url.toLowerCase();
    for (const ext of ARTIFACT_EXTENSIONS) {
        if (lowerCaseUrl.endsWith(ext)) {
            return ext.toUpperCase().substring(1);
        }
    }
    return "URL"; // Default to "URL"
};

export const ReferenceViewer = ({
    references,
    additionalReferences = [],
    showDisclaimer = false,
}: {
    references: Reference[];
    additionalReferences?: Reference[];
    showDisclaimer: boolean;
}) => {
    const { colorScheme } = useMantineColorScheme();
    const viewport = useRef<HTMLDivElement>(null);
    const [showAdditional, setShowAdditional] = useState(false);

    if (!references.length && !additionalReferences.length) {
        return (
            <Box p="md" ta="center">
                <Text size="sm" c="dimmed">
                    No reference documents available for this answer.
                </Text>
            </Box>
        );
    }

    return (
        <Box h="100%">
            <ScrollArea.Autosize
                viewportRef={viewport}
                scrollbarSize={4}
                type="hover"
                style={{ height: "100%" }}
            >
                <Box
                    style={(theme) => ({
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                        gap: theme.spacing.md,
                        padding: `${theme.spacing.xs}px 0`,
                    })}
                >
                    {references.map((thisReference, index) => (
                        <Box
                            key={`${thisReference.title}-${index}`}
                            style={{ position: "relative", height: "100%" }}
                        >
                            <ReferenceTooltip
                                reference={thisReference}
                                showDisclaimer={showDisclaimer}
                            />
                        </Box>
                    ))}

                    {additionalReferences.length > 0 && !showAdditional && (
                        <UnstyledButton
                            onClick={() => setShowAdditional(true)}
                            style={(theme) => ({
                                padding: theme.spacing.md,
                                borderRadius: theme.radius.sm,
                                backgroundColor:
                                    colorScheme === "dark"
                                        ? theme.colors.dark[6]
                                        : theme.colors.gray[0],
                                minHeight: 70,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                border: `1px solid ${
                                    colorScheme === "dark"
                                        ? theme.colors.dark[4]
                                        : theme.colors.gray[2]
                                }`,
                                "&:hover": {
                                    backgroundColor:
                                        colorScheme === "dark"
                                            ? theme.colors.dark[5]
                                            : theme.colors.gray[1],
                                    cursor: "pointer",
                                    transform: "translateY(-1px)",
                                    boxShadow: theme.shadows.sm,
                                },
                                transition: "all 150ms ease",
                            })}
                        >
                            <IconChevronsRight size={32} />
                            <Text size="xs" c="dimmed" ta="center" mt={4}>
                                Show Additional Sources ({additionalReferences.length})
                            </Text>
                        </UnstyledButton>
                    )}

                    {showAdditional && (
                        <>
                            {additionalReferences.map((thisReference, index) => (
                                <Box
                                    key={`additional-${thisReference.title}-${index}`}
                                    style={{ position: "relative", height: "100%" }}
                                >
                                    <ReferenceTooltip
                                        reference={thisReference}
                                        showDisclaimer={showDisclaimer}
                                    />
                                </Box>
                            ))}
                            <UnstyledButton
                                onClick={() => setShowAdditional(false)}
                                style={(theme) => ({
                                    padding: theme.spacing.md,
                                    borderRadius: theme.radius.sm,
                                    backgroundColor:
                                        colorScheme === "dark"
                                            ? theme.colors.dark[6]
                                            : theme.colors.gray[0],
                                    minHeight: 70,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    border: `1px solid ${
                                        colorScheme === "dark"
                                            ? theme.colors.dark[4]
                                            : theme.colors.gray[2]
                                    }`,
                                    "&:hover": {
                                        backgroundColor:
                                            colorScheme === "dark"
                                                ? theme.colors.dark[5]
                                                : theme.colors.gray[1],
                                        cursor: "pointer",
                                        transform: "translateY(-1px)",
                                        boxShadow: theme.shadows.sm,
                                    },
                                    transition: "all 150ms ease",
                                })}
                            >
                                <IconChevronsLeft size={32} />
                                <Text size="xs" c="dimmed" ta="center" mt={4}>
                                    Hide Additional Sources
                                </Text>
                            </UnstyledButton>
                        </>
                    )}
                </Box>
            </ScrollArea.Autosize>
        </Box>
    );
};

export const ReferenceTooltip = ({
    reference,
    showDisclaimer,
}: {
    reference: Reference;
    showDisclaimer: boolean;
}) => {
    const { data: datasources = [] } = useDatasources();
    const label = getDatasourceLabel(reference.dataSourceType, datasources);
    const page_numbers = Array.from(reference.page_numbers).join(", ");
    const [isLoading, setIsLoading] = useState(false);
    const { getFullUrl, handleArtifactClick } = useArtifactHandler();
    const { openDisclaimer } = useDisclaimer();
    const { colorScheme } = useMantineColorScheme();

    const handleClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (!reference.source || isLoading) {
            e.preventDefault();
            return;
        }
        setIsLoading(true);
        try {
            await handleArtifactClick(
                e,
                reference.source,
                showDisclaimer ? getDisclaimerMessages(reference.dataSourceType) : [],
                openDisclaimer,
            );
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <UnstyledButton
            component="a"
            data-testid={reference.reference_id}
            href={reference.source ? getFullUrl(reference.source) : undefined}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            onAuxClick={onMiddleClick(handleClick)}
            title={reference.title}
            style={(theme) => ({
                color: "black",
                padding: theme.spacing.sm,
                borderRadius: theme.radius.sm,
                backgroundColor:
                    colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[0],
                minHeight: 70,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                border: `1px solid ${colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2]}`,
                "&:hover": {
                    backgroundColor:
                        reference.source && !isLoading
                            ? colorScheme === "dark"
                                ? theme.colors.dark[5]
                                : theme.colors.gray[1]
                            : "inherit",
                    cursor: reference.source && !isLoading ? "pointer" : "default",
                    boxShadow: reference.source && !isLoading ? theme.shadows.xs : "none",
                },
                opacity: reference.source ? 1 : 0.5,
                width: "100%",
                transition: "background-color 150ms ease, box-shadow 150ms ease",
            })}
        >
            {isLoading && (
                <Box
                    style={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        zIndex: 1,
                    }}
                >
                    <Loader size="xs" />
                </Box>
            )}

            <Text
                size="xs"
                fw={500}
                style={{
                    width: "100%",
                    marginBottom: "auto",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                }}
            >
                <Text
                    component="span"
                    style={{
                        lineHeight: 1.5,
                        flex: 1,
                        minWidth: 0,
                    }}
                    lineClamp={2}
                >
                    {reference.title ||
                        (() => {
                            // Fallback to a meaningful name from the source URL
                            if (!reference.source) return "Untitled Reference";

                            // Try to extract filename from URL
                            const urlParts = reference.source.split("/");
                            const lastPart = urlParts[urlParts.length - 1];

                            // Clean up the filename
                            const fileName = lastPart
                                .replace(/\.pdf$/i, "")
                                .replace(/%20/g, " ")
                                .replace(/[?&].*$/, ""); // Remove query parameters

                            // If it's a Salesforce link, extract the ID
                            if (reference.source.includes("salesforce.com")) {
                                const sfId = lastPart;
                                return `Salesforce Case: ${sfId}`;
                            }

                            // If it's an ADS link
                            if (reference.source.includes("ads-")) {
                                return `ADS Query: ${fileName}`;
                            }

                            return fileName || "Untitled Reference";
                        })()}
                </Text>
            </Text>

            <Box
                style={(theme) => ({
                    marginTop: theme.spacing.xs,
                    paddingTop: theme.spacing.xs,
                    borderTop: `1px solid ${colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2]}`,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: theme.spacing.xs,
                })}
            >
                {label && (
                    <Group gap={4} style={{ flex: 1, minWidth: 0 }}>
                        {label && (
                            <Text
                                size="xs"
                                c="dimmed"
                                style={{
                                    fontSize: "10px",
                                    letterSpacing: "0.3px",
                                    lineHeight: 1.5,
                                }}
                                truncate
                            >
                                {label}
                            </Text>
                        )}
                    </Group>
                )}
                {getFileExtension(reference.source) && (
                    <Text
                        style={(theme) => ({
                            padding: "2px 6px",
                            borderRadius: theme.radius.sm,
                            backgroundColor:
                                colorScheme === "dark"
                                    ? theme.colors.dark[4]
                                    : theme.colors.gray[2],
                            color:
                                colorScheme === "dark"
                                    ? theme.colors.dark[0]
                                    : theme.colors.gray[7],
                            fontSize: "10px",
                            letterSpacing: "0.5px",
                            flexShrink: 0,
                            lineHeight: 1.5,
                        })}
                    >
                        {getFileExtension(reference.source)}
                    </Text>
                )}
            </Box>
            <Text>
                {page_numbers && (
                    <Text
                        size="xs"
                        c="dimmed"
                        style={{
                            fontSize: "10px",
                            letterSpacing: "0.3px",
                            lineHeight: 1.5,
                        }}
                        truncate
                    >
                        Pages: ({page_numbers})
                    </Text>
                )}
            </Text>
        </UnstyledButton>
    );
};
